import { Component, ErrorInfo, ReactNode } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import i18next from 'i18next'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)

    this.setState({
      error,
      errorInfo,
    })
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      const t = (key: string) => i18next.t(key as never, { ns: 'common' })

      return (
        <div className="flex min-h-screen items-center justify-center bg-background p-4">
          <div className="w-full max-w-md space-y-6 rounded-lg border border-destructive/20 bg-card p-8 shadow-lg">
            <div className="flex items-center gap-3 text-destructive">
              <AlertTriangle className="h-8 w-8" />
              <h1 className="text-heading">{t('error.somethingWentWrong')}</h1>
            </div>

            <div className="space-y-2">
              <p className="text-body text-muted-foreground">
                {t('error.unexpectedError')}
              </p>

              {this.state.error && (
                <details className="mt-4 rounded-md bg-muted p-3">
                  <summary className="cursor-pointer text-sm font-medium">
                    {t('error.errorDetails')}
                  </summary>
                  <div className="mt-2 space-y-2 text-xs font-mono">
                    <p className="text-destructive">{this.state.error.message}</p>
                    {this.state.errorInfo && (
                      <pre className="overflow-auto whitespace-pre-wrap break-words">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    )}
                  </div>
                </details>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={this.handleReset}
                className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              >
                <RefreshCw className="h-4 w-4" />
                {t('actions.retry')}
              </button>

              <button
                onClick={() => window.location.href = '/'}
                className="rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground"
              >
                {t('actions.goHome')}
              </button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
